import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../../components/Navbar";
import { Footer } from "../../components/Footer";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { 
  FileText, ArrowLeft, ArrowRight, Briefcase, Clock, 
  CheckCircle, XCircle, HelpCircle, Camera, Languages, Award, AlertTriangle
} from "lucide-react";

// Schema.org Article structured data
const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Come scrivere un Curriculum efficace per le aziende ticinesi",
  "description": "Guida completa al CV svizzero: foto professionale, certificati di lavoro, competenze linguistiche. Scopri i 3 errori da evitare per il tuo Modello CV Ticino.",
  "author": {
    "@type": "Organization",
    "name": "LavoroTicino.ch"
  },
  "publisher": {
    "@type": "Organization",
    "name": "LavoroTicino.ch"
  },
  "datePublished": "2026-01-15",
  "dateModified": "2026-01-15",
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": "https://lavoroticino.ch/guide/cv-svizzero"
  },
  "keywords": "Modello CV Ticino, Curriculum Svizzera, CV professionale, CV svizzero"
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Devo inserire la foto nel CV svizzero?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Sì, in Svizzera la foto è fortemente consigliata e quasi sempre richiesta. Deve essere professionale, su sfondo neutro, con abbigliamento formale."
      }
    },
    {
      "@type": "Question",
      "name": "Cosa sono i certificati di lavoro svizzeri?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "I certificati di lavoro (Arbeitszeugnis) sono documenti rilasciati dai precedenti datori di lavoro che descrivono le mansioni svolte e le prestazioni. In Svizzera sono essenziali e vanno allegati al CV."
      }
    },
    {
      "@type": "Question",
      "name": "In quale lingua devo scrivere il CV per il Ticino?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Per il Canton Ticino, il CV va scritto in italiano. Tuttavia, se l'azienda è internazionale o l'annuncio è in altra lingua, prepara anche una versione in tedesco, francese o inglese."
      }
    }
  ]
};

export default function CVSvizzeroGuide() {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify([articleSchema, faqSchema]);
    document.head.appendChild(script);
    window.scrollTo(0, 0);
    return () => document.head.removeChild(script);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Link to="/guide-lavoro" className="hover:text-[#004E64]">Guide</Link>
            <span>/</span>
            <span className="text-slate-900">CV Svizzero</span>
          </div>
        </div>
      </div>

      {/* Article Header */}
      <header className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6" />
            </div>
            <span className="text-sm font-medium text-white/80 uppercase tracking-wider">
              Guida Pratica
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
            Come scrivere un Curriculum<br />
            efficace per le aziende ticinesi
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mb-6">
            Elementi obbligatori del CV svizzero: foto professionale, certificati di lavoro 
            e competenze linguistiche. I 3 errori da evitare.
          </p>
          <div className="flex items-center gap-4 text-sm text-white/70">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" /> 6 min di lettura
            </span>
            <span>•</span>
            <span>Aggiornato: Gennaio 2026</span>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="py-10 sm:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg prose-slate max-w-none">
            
            {/* Intro */}
            <p className="text-xl text-slate-700 leading-relaxed mb-8">
              Il <strong>Curriculum Vitae svizzero</strong> ha caratteristiche specifiche che lo 
              differenziano dal CV italiano o europeo. Per avere successo nella ricerca di lavoro 
              in <strong>Canton Ticino</strong>, è fondamentale adattare il proprio CV alle aspettative 
              locali.
            </p>

            {/* Section 1 - Photo */}
            <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-sm font-bold">1</span>
              Foto Professionale
            </h2>
            
            <div className="flex items-start gap-4 bg-emerald-50 p-6 rounded-xl mb-6">
              <Camera className="w-10 h-10 text-emerald-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-emerald-900 mb-2">Obbligatoria in Svizzera</h3>
                <p className="text-emerald-800 m-0">
                  A differenza di altri paesi, in Svizzera la foto sul CV è <strong>fortemente 
                  consigliata</strong> e spesso richiesta esplicitamente. Un CV senza foto potrebbe 
                  essere scartato immediatamente.
                </p>
              </div>
            </div>

            <h3 className="text-xl font-semibold mt-6 mb-3">Requisiti della foto:</h3>
            <ul className="space-y-2 mb-6">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span><strong>Sfondo neutro:</strong> bianco, grigio chiaro o blu</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span><strong>Abbigliamento professionale:</strong> giacca e cravatta per uomini, tailleur per donne</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span><strong>Qualità alta:</strong> evita selfie o foto ritagliate</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span><strong>Espressione:</strong> sorriso leggero, sguardo diretto</span>
              </li>
            </ul>

            {/* Section 2 - Certificates */}
            <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center text-sm font-bold">2</span>
              Certificati di Lavoro (Arbeitszeugnis)
            </h2>

            <div className="flex items-start gap-4 bg-amber-50 p-6 rounded-xl mb-6">
              <Award className="w-10 h-10 text-amber-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-amber-900 mb-2">Elemento distintivo svizzero</h3>
                <p className="text-amber-800 m-0">
                  Gli <strong>Arbeitszeugnis</strong> sono lettere di referenza dettagliate rilasciate 
                  dai precedenti datori di lavoro. In Svizzera sono considerati <strong>essenziali</strong> 
                  e vanno sempre allegati al CV.
                </p>
              </div>
            </div>

            <p>
              Ogni certificato dovrebbe contenere: periodo di impiego, mansioni svolte, valutazione 
              delle prestazioni e comportamento sul lavoro. Se hai lavorato in Italia, chiedi ai 
              tuoi ex datori di lavoro una <strong>lettera di referenze</strong> equivalente.
            </p>

            {/* Section 3 - Languages */}
            <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">3</span>
              Competenze Linguistiche
            </h2>

            <div className="flex items-start gap-4 bg-blue-50 p-6 rounded-xl mb-6">
              <Languages className="w-10 h-10 text-blue-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-blue-900 mb-2">Sezione fondamentale</h3>
                <p className="text-blue-800 m-0">
                  La Svizzera ha 4 lingue ufficiali. Nel <strong>Modello CV Ticino</strong>, specifica 
                  sempre il tuo livello in italiano, tedesco, francese e inglese usando la scala europea.
                </p>
              </div>
            </div>

            <h3 className="text-xl font-semibold mt-6 mb-3">Lingue più richieste in Ticino:</h3>
            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="px-4 py-3 text-left font-semibold">Lingua</th>
                    <th className="px-4 py-3 text-left font-semibold">Importanza</th>
                    <th className="px-4 py-3 text-left font-semibold">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr className="bg-slate-50">
                    <td className="px-4 py-3 font-medium">Italiano</td>
                    <td className="px-4 py-3"><span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-sm">Essenziale</span></td>
                    <td className="px-4 py-3 text-sm">Lingua principale del Canton Ticino</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Tedesco</td>
                    <td className="px-4 py-3"><span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-sm">Molto richiesto</span></td>
                    <td className="px-4 py-3 text-sm">Per aziende nazionali e banche</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-4 py-3 font-medium">Inglese</td>
                    <td className="px-4 py-3"><span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">Importante</span></td>
                    <td className="px-4 py-3 text-sm">Aziende internazionali e tech</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Francese</td>
                    <td className="px-4 py-3"><span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-sm">Utile</span></td>
                    <td className="px-4 py-3 text-sm">Per ruoli con clientela romanda</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p>
              Usa la <strong>scala QCER</strong> (A1, A2, B1, B2, C1, C2) per indicare il livello. 
              Evita termini vaghi come "buono" o "scolastico".
            </p>

            {/* Section 4 - 3 Errors */}
            <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              I 3 Errori da Evitare
            </h2>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-4 p-5 bg-red-50 border border-red-200 rounded-xl">
                <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <h3 className="font-bold text-red-900 mb-1">CV troppo lungo</h3>
                  <p className="text-red-800 m-0 text-sm">
                    In Svizzera il CV dovrebbe essere di <strong>2-3 pagine massimo</strong>. 
                    I recruiter dedicano circa 30 secondi alla prima lettura. Sii conciso e mirato.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-red-50 border border-red-200 rounded-xl">
                <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <h3 className="font-bold text-red-900 mb-1">Mancanza di dati concreti</h3>
                  <p className="text-red-800 m-0 text-sm">
                    Evita descrizioni generiche. Usa <strong>numeri e risultati</strong>: "Aumentato 
                    le vendite del 25%" è meglio di "Responsabile delle vendite".
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-red-50 border border-red-200 rounded-xl">
                <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <h3 className="font-bold text-red-900 mb-1">Formato Europass</h3>
                  <p className="text-red-800 m-0 text-sm">
                    Il formato Europass <strong>non è apprezzato in Svizzera</strong>. Crea un CV 
                    personalizzato con un design pulito e professionale. Evita template troppo creativi.
                  </p>
                </div>
              </div>
            </div>

            {/* Checklist */}
            <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">
              Checklist del CV Perfetto
            </h2>

            <div className="bg-slate-50 p-6 rounded-xl mb-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  "Foto professionale",
                  "Dati personali completi",
                  "Profilo/Obiettivo (2-3 righe)",
                  "Esperienze in ordine cronologico inverso",
                  "Formazione e certificazioni",
                  "Competenze linguistiche (scala QCER)",
                  "Competenze tecniche",
                  "Certificati di lavoro allegati",
                  "Data e firma",
                  "Formato PDF"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-12 pt-8 border-t">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-[#004E64]" />
              Domande Frequenti
            </h2>

            <div className="space-y-4">
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <h3 className="font-bold text-slate-900 mb-2">Devo inserire la foto nel CV svizzero?</h3>
                  <p className="text-slate-600">
                    <strong>Sì</strong>, in Svizzera la foto è fortemente consigliata e quasi sempre richiesta. 
                    Deve essere professionale, su sfondo neutro, con abbigliamento formale. Un CV senza foto 
                    rischia di essere scartato immediatamente.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <h3 className="font-bold text-slate-900 mb-2">Cosa sono i certificati di lavoro svizzeri?</h3>
                  <p className="text-slate-600">
                    I <strong>certificati di lavoro</strong> (Arbeitszeugnis) sono documenti rilasciati dai 
                    precedenti datori di lavoro che descrivono le mansioni svolte e le prestazioni. In Svizzera 
                    sono essenziali e vanno allegati al CV. Includono periodo, ruolo, valutazione e comportamento.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <h3 className="font-bold text-slate-900 mb-2">In quale lingua devo scrivere il CV per il Ticino?</h3>
                  <p className="text-slate-600">
                    Per il <strong>Canton Ticino</strong>, il CV va scritto in italiano. Tuttavia, se l'azienda 
                    è internazionale o l'annuncio è in altra lingua, prepara anche una versione in tedesco, 
                    francese o inglese. Adatta sempre la lingua a quella dell'annuncio.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* CTA Banner */}
          <div className="mt-12 bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-2xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-3">Pronto a candidarti?</h3>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Ora che hai un CV perfetto, guarda le ultime offerte di lavoro su LavoroTicino.ch 
              e trova l'opportunità perfetta per te.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/jobs">
                <Button size="lg" className="bg-white text-emerald-700 hover:bg-white/90">
                  <Briefcase className="w-5 h-5 mr-2" />
                  Vedi Offerte di Lavoro
                </Button>
              </Link>
              <Link to="/register?type=candidate">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  Iscriviti Gratis
                </Button>
              </Link>
            </div>
          </div>

          {/* Navigation */}
          <div className="mt-10 flex items-center justify-between pt-6 border-t">
            <Link to="/guide/permessi-lavoro" className="flex items-center gap-2 text-slate-600 hover:text-[#004E64]">
              <ArrowLeft className="w-4 h-4" /> Permessi di Lavoro
            </Link>
            <Link to="/guide/stipendio-netto" className="flex items-center gap-2 text-[#004E64] font-medium hover:underline">
              Prossima: Stipendio Netto <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
